import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";
import { Label } from "@tql/ui-components-react/kendo/labels";
import type { ReactNode } from "react";

import {
	FieldType,
	ValidationInputField,
} from "../fields/validation-input-field.tsx";

export type TenderLabelOption = {
	displayName: string;
	labelSize: string;
};

export type TenderDocumentOptionsClassNames = {
	additional: string;
	additionalHeader: string;
	characterCount: string;
	input: string;
	label: string;
	labelCountInput: string;
	row: string;
	sizeInput: string;
};

type TenderDocumentOptionsFieldsProps = {
	additionalDetailsId: string;
	additionalDetailsLabel: ReactNode;
	additionalDetailsMaxLength: number;
	additionalDetailsName: string;
	additionalDetailsValidator: (value?: string) => string;
	characterCountAriaLive?: "off" | "polite" | "assertive";
	betweenFieldsAndAdditional?: ReactNode;
	classNames: TenderDocumentOptionsClassNames;
	formRenderProps: FormRenderProps;
	isLoading: boolean;
	labelCountId: string;
	labelCountLabel: ReactNode;
	labelCountName: string;
	labelCountValidator: (value: number | null) => string;
	labelOptions: readonly TenderLabelOption[];
	labelSizeId: string;
	labelSizeName: string;
	labelSizeValidator: (value: TenderLabelOption | null) => string;
};

const joinClassNames = (...classNames: string[]): string =>
	classNames.filter((className) => className.length > 0).join(" ");

export const TenderDocumentOptionsFields = ({
	additionalDetailsId,
	additionalDetailsLabel,
	additionalDetailsMaxLength,
	additionalDetailsName,
	additionalDetailsValidator,
	characterCountAriaLive,
	betweenFieldsAndAdditional,
	classNames,
	formRenderProps,
	isLoading,
	labelCountId,
	labelCountLabel,
	labelCountName,
	labelCountValidator,
	labelOptions,
	labelSizeId,
	labelSizeName,
	labelSizeValidator,
}: TenderDocumentOptionsFieldsProps) => {
	const additionalDetails =
		(formRenderProps.valueGetter(additionalDetailsName) as string) || "";

	return (
		<>
			<div className={classNames.row}>
				<Label className={classNames.label} editorId={labelSizeId}>
					Size <span className={"required"}>*</span>
				</Label>
				<div className={joinClassNames(classNames.input, classNames.sizeInput)}>
					<ValidationInputField
						data={labelOptions}
						disabled={isLoading}
						fieldType={FieldType.DROPDOWN}
						id={labelSizeId}
						name={labelSizeName}
						textField={"displayName"}
						validator={labelSizeValidator}
					/>
				</div>
			</div>

			<div className={classNames.row}>
				<Label className={classNames.label} editorId={labelCountId}>
					{labelCountLabel} <span className={"required"}>*</span>
				</Label>
				<div
					className={joinClassNames(
						classNames.input,
						classNames.labelCountInput,
					)}
				>
					<ValidationInputField
						disabled={isLoading}
						fieldType={FieldType.NUMERIC}
						id={labelCountId}
						name={labelCountName}
						validator={labelCountValidator}
					/>
				</div>
			</div>

			{betweenFieldsAndAdditional}

			<div className={classNames.additional}>
				<div className={classNames.additionalHeader}>
					<Label className={classNames.label} editorId={additionalDetailsId}>
						{additionalDetailsLabel}
					</Label>
					<span
						aria-live={characterCountAriaLive}
						className={classNames.characterCount}
					>
						({additionalDetails.length}/{additionalDetailsMaxLength})
					</span>
				</div>
				<ValidationInputField
					disabled={isLoading}
					fieldType={FieldType.TEXTAREA}
					id={additionalDetailsId}
					maxLength={additionalDetailsMaxLength}
					name={additionalDetailsName}
					rows={4}
					validator={additionalDetailsValidator}
				/>
			</div>
		</>
	);
};
